import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { categories, articles } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'movies-tv');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = articles.filter((a) => a.category === activeSlug);

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={currentCategory.name}
        description={`欧美一区${currentCategory.name}频道 - ${currentCategory.description}，为您提供最全面的欧美一区${currentCategory.name}资源导航与内容推荐服务。`}
        keywords={`欧美一区${currentCategory.name},欧美一区高清资源,欧美一区在线观看,欧美一区内容推荐,${currentCategory.name}推荐`}
        canonicalPath={`/category/${activeSlug}`}
        ogType="website"
        breadcrumbs={[
          { label: '首页', href: '/' },
          { label: currentCategory.name, href: `/category/${activeSlug}` },
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `欧美一区${currentCategory.name}频道`,
          description: currentCategory.description,
          url: `https://www.oumei1.com/category/${activeSlug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '欧美一区精选内容导航平台',
            url: 'https://www.oumei1.com/',
          },
        }}
      />

      <main>
        <Navbar />

        <div className="pt-16 md:pt-18 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
              <Link to="/" className="hover:text-foreground-800 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">{currentCategory.name}</span>
            </nav>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              欧美一区{currentCategory.name}
            </h1>
            <p className="text-sm text-foreground-600 max-w-2xl">
              {currentCategory.description} — 汇聚海量优质欧美一区{currentCategory.name}资源，畅享高清视听体验。
            </p>
          </div>
        </div>

        <div className="border-b border-background-200/70 bg-background-50 sticky top-16 z-40">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 py-3 overflow-x-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex-shrink-0 ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="py-10 md:py-14 bg-background-50 min-h-[50vh]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-white rounded-lg overflow-hidden border border-background-200/70 hover:border-background-300/60 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors mb-2">
                      {article.title}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i> {article.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i> {article.views.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {categoryArticles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-sm text-foreground-500">该分类下暂无内容，敬请期待</p>
              </div>
            )}
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}